import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import ConfirmModal from './common/ConfirmModal';
import './OrderManager.css';

const OrderManager = () => {
  const [activeTab, setActiveTab] = useState('products'); // 'products' or 'workshops'
  const [orders, setOrders] = useState([]);
  const [workshopBookings, setWorkshopBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    paymentStatus: '',
    page: 1,
    limit: 20
  });
  const [bookingFilters, setBookingFilters] = useState({
    status: '',
    page: 1,
    limit: 20
  });
  const [totalPages, setTotalPages] = useState(1);
  const [bookingTotalPages, setBookingTotalPages] = useState(1);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'danger'
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (activeTab === 'products') {
      fetchOrders();
    } else {
      fetchWorkshopBookings();
    }
    // Close modals when switching tabs
    setShowOrderModal(false);
    setShowBookingModal(false);
  }, [filters, bookingFilters, activeTab]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.paymentStatus) params.paymentStatus = filters.paymentStatus;
      params.page = filters.page;
      params.limit = filters.limit;

      const response = await axios.get(`${API_URL}/api/orders`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      setOrders(response.data.data);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      toast.error('Failed to fetch orders');
      console.error('Orders error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkshopBookings = async () => {
    setLoading(true);
    try {
      const params = {};
      if (bookingFilters.status) params.status = bookingFilters.status;
      params.page = bookingFilters.page;
      params.limit = bookingFilters.limit;

      const response = await axios.get(`${API_URL}/api/workshop-bookings`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      setWorkshopBookings(response.data.data);
      setBookingTotalPages(response.data.totalPages);
    } catch (error) {
      toast.error('Failed to fetch workshop bookings');
      console.error('Workshop bookings error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrder = async (orderId) => {
    try {
      const response = await axios.get(`${API_URL}/api/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedOrder(response.data.data);
      setShowOrderModal(true);
    } catch (error) {
      toast.error('Failed to fetch order details');
    }
  };

  const handleUpdateOrder = async (orderId, updates) => {
    try {
      await axios.put(`${API_URL}/api/orders/${orderId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order updated successfully');
      fetchOrders();
      if (showOrderModal) {
        handleViewOrder(orderId);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update order');
    }
  };

  const handleCancelOrder = (order) => {
    setConfirmModal({
      show: true,
      title: 'Cancel Order',
      message: `Are you sure you want to cancel order ${order.orderNumber}? This action will restore product stock.`,
      type: 'danger',
      onConfirm: async () => {
        try {
          await handleUpdateOrder(order._id, {
            status: 'cancelled',
            cancelledReason: 'Cancelled by admin'
          });
          setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'danger' });
        } catch (error) {
          // Error already handled in handleUpdateOrder
        }
      }
    });
  };

  const formatCurrency = (amount, currency = 'INR') => {
    const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '₹';
    return `${symbol}${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      pending: 'badge-pending',
      confirmed: 'badge-confirmed',
      processing: 'badge-processing',
      shipped: 'badge-shipped',
      delivered: 'badge-delivered',
      cancelled: 'badge-cancelled'
    };
    return statusMap[status] || 'badge-default';
  };

  const getPaymentBadgeClass = (status) => {
    const statusMap = {
      pending: 'badge-pending',
      processing: 'badge-processing',
      completed: 'badge-completed',
      failed: 'badge-failed',
      refunded: 'badge-refunded'
    };
    return statusMap[status] || 'badge-default';
  };

  const getBookingStatusBadgeClass = (status) => {
    const statusMap = {
      pending: 'badge-pending',
      confirmed: 'badge-confirmed',
      cancelled: 'badge-cancelled',
      completed: 'badge-completed'
    };
    return statusMap[status] || 'badge-default';
  };

  const getShippingStatusBadgeClass = (status) => {
    const statusMap = {
      not_shipped: 'badge-pending',
      preparing: 'badge-processing',
      shipped: 'badge-confirmed',
      out_for_delivery: 'badge-processing',
      delivered: 'badge-delivered'
    };
    return statusMap[status] || 'badge-default';
  };

  const handleViewBooking = async (bookingId) => {
    try {
      const response = await axios.get(`${API_URL}/api/workshop-bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedBooking(response.data.data);
      setShowBookingModal(true);
    } catch (error) {
      toast.error('Failed to fetch booking details');
    }
  };

  const handleUpdateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API_URL}/api/workshop-bookings/${bookingId}/status`, 
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Booking status updated successfully');
      fetchWorkshopBookings();
      if (showBookingModal) {
        handleViewBooking(bookingId);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update booking status');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDownloadInvoice = async (orderId) => {
    try {
      const response = await axios.get(`${API_URL}/api/invoices/${orderId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Get order number for filename
      const order = orders.find(o => o._id === orderId);
      const orderNumber = order?.orderNumber || orderId;

      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice-${orderNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to download invoice');
    }
  };

  const handleEmailInvoice = async (orderId) => {
    try {
      const response = await axios.post(`${API_URL}/api/invoices/${orderId}/email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Invoice sent successfully');
      } else {
        toast.error(response.data.message || 'Failed to send invoice');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send invoice');
    }
  };

  if (loading && activeTab === 'products' && orders.length === 0) {
    return (
      <div className="order-manager">
        <div className="loading-container">
          <p>Loading orders...</p>
        </div>
      </div>
    );
  }

  if (loading && activeTab === 'workshops' && workshopBookings.length === 0) {
    return (
      <div className="order-manager">
        <div className="loading-container">
          <p>Loading workshop bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="order-manager">
      <div className="order-header">
        <div className="order-header-top">
          <h1>Order Management</h1>
          <div className="order-filters">
          {activeTab === 'products' ? (
            <>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
                className="filter-select"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select
                value={filters.paymentStatus}
                onChange={(e) => setFilters({ ...filters, paymentStatus: e.target.value, page: 1 })}
                className="filter-select"
              >
                <option value="">All Payment Statuses</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
              </select>
            </>
          ) : (
            <select
              value={bookingFilters.status}
              onChange={(e) => setBookingFilters({ ...bookingFilters, status: e.target.value, page: 1 })}
              className="filter-select"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
              <option value="completed">Completed</option>
            </select>
          )}
          </div>
        </div>
        {/* Tabs */}
        <div className="order-tabs">
          <button
            className={`order-tab ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            📦 Product Orders
          </button>
          <button
            className={`order-tab ${activeTab === 'workshops' ? 'active' : ''}`}
            onClick={() => setActiveTab('workshops')}
          >
            🎓 Workshop Bookings
          </button>
        </div>
      </div>

      {/* Product Orders Table */}
      {activeTab === 'products' && (
        <div className="orders-table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Total</th>
                <th>Status</th>
                <th>Delivery</th>
                <th>Payment</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan="9" className="no-orders">
                    No orders found
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                <tr key={order._id}>
                  <td>
                    <strong>{order.orderNumber}</strong>
                  </td>
                  <td>
                    <div className="customer-info">
                      <div>{order.customer.name}</div>
                      <small>{order.customer.email}</small>
                      <small>{order.customer.phone}</small>
                    </div>
                  </td>
                  <td>
                    <div className="items-count">
                      {order.items.length} item(s)
                    </div>
                  </td>
                  <td>
                    <strong>{formatCurrency(order.total, order.currency)}</strong>
                  </td>
                  <td>
                    <span className={`badge ${getStatusBadgeClass(order.status)}`}>
                      {order.status}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${getShippingStatusBadgeClass(order.shippingStatus || 'not_shipped')}`}>
                      {order.shippingStatus ? order.shippingStatus.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Not Shipped'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${getPaymentBadgeClass(order.payment.status)}`}>
                      {order.payment.status}
                    </span>
                  </td>
                  <td>
                    {new Date(order.createdAt).toLocaleDateString()}
                    <br />
                    <small>{new Date(order.createdAt).toLocaleTimeString()}</small>
                  </td>
                  <td>
                    <div className="order-actions">
                      <button
                        onClick={() => handleViewOrder(order._id)}
                        className="btn btn-sm btn-primary"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleDownloadInvoice(order._id)}
                        className="btn btn-sm btn-secondary"
                        title="Download Invoice"
                      >
                        📄 Invoice
                      </button>
                      {order.status !== 'cancelled' && order.status !== 'delivered' && (
                        <button
                          onClick={() => handleCancelOrder(order)}
                          className="btn btn-sm btn-danger"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Workshop Bookings Table */}
      {activeTab === 'workshops' && (
        <div className="orders-table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Booking ID</th>
                <th>Workshop</th>
                <th>Customer</th>
                <th>Contact</th>
                <th>Skill Level</th>
                <th>Status</th>
                <th>Payment</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {workshopBookings.length === 0 ? (
                <tr>
                  <td colSpan="9" className="no-orders">
                    No workshop bookings found
                  </td>
                </tr>
              ) : (
                workshopBookings.map((booking) => (
                  <tr key={booking._id}>
                    <td>
                      <strong>#{booking._id.toString().slice(-8)}</strong>
                    </td>
                    <td>
                      <div className="workshop-info">
                        <div>{booking.workshop?.title || 'N/A'}</div>
                        {booking.workshop?.date && (
                          <small>{formatDate(booking.workshop.date)}</small>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="customer-info">
                        <div>{booking.name}</div>
                        <small>Age: {booking.age}</small>
                      </div>
                    </td>
                    <td>
                      <div className="customer-info">
                        <small>{booking.email}</small>
                        <small>{booking.mobile}</small>
                      </div>
                    </td>
                    <td>
                      <span className="badge badge-info">{booking.skillLevel}</span>
                    </td>
                    <td>
                      <span className={`badge ${getBookingStatusBadgeClass(booking.status)}`}>
                        {booking.status}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${getPaymentBadgeClass(booking.paymentStatus)}`}>
                        {booking.paymentStatus}
                      </span>
                    </td>
                    <td>
                      {formatDate(booking.createdAt)}
                    </td>
                    <td>
                      <div className="order-actions">
                        <button
                          onClick={() => handleViewBooking(booking._id)}
                          className="btn btn-sm btn-primary"
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {activeTab === 'products' && totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
            disabled={filters.page === 1}
            className="btn btn-secondary"
          >
            Previous
          </button>
          <span>
            Page {filters.page} of {totalPages}
          </span>
          <button
            onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
            disabled={filters.page >= totalPages}
            className="btn btn-secondary"
          >
            Next
          </button>
        </div>
      )}

      {activeTab === 'workshops' && bookingTotalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setBookingFilters({ ...bookingFilters, page: bookingFilters.page - 1 })}
            disabled={bookingFilters.page === 1}
            className="btn btn-secondary"
          >
            Previous
          </button>
          <span>
            Page {bookingFilters.page} of {bookingTotalPages}
          </span>
          <button
            onClick={() => setBookingFilters({ ...bookingFilters, page: bookingFilters.page + 1 })}
            disabled={bookingFilters.page >= bookingTotalPages}
            className="btn btn-secondary"
          >
            Next
          </button>
        </div>
      )}

      {/* Order Detail Modal */}
      {showOrderModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => setShowOrderModal(false)}>
          <div className="modal-content order-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Order Details - {selectedOrder.orderNumber}</h2>
              <button className="modal-close" onClick={() => setShowOrderModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="order-details-grid">
                <div className="order-section">
                  <h3>Customer Information</h3>
                  <div className="detail-item">
                    <strong>Name:</strong> {selectedOrder.customer.name}
                  </div>
                  <div className="detail-item">
                    <strong>Email:</strong> {selectedOrder.customer.email}
                  </div>
                  <div className="detail-item">
                    <strong>Phone:</strong> {selectedOrder.customer.phone}
                  </div>
                  <div className="detail-item">
                    <strong>Address:</strong>
                    <div className="address-block">
                      {selectedOrder.customer.address.street}<br />
                      {selectedOrder.customer.address.city}, {selectedOrder.customer.address.state}<br />
                      {selectedOrder.customer.address.zipCode}, {selectedOrder.customer.address.country}
                    </div>
                  </div>
                </div>

                <div className="order-section">
                  <h3>Order Items</h3>
                  <div className="items-list">
                    {selectedOrder.items.map((item, index) => (
                      <div key={index} className="order-item">
                        <div className="item-image">
                          {item.product?.images?.[0] ? (
                            <img src={item.product.images[0]} alt={item.name} />
                          ) : (
                            <div className="no-image">No Image</div>
                          )}
                        </div>
                        <div className="item-details">
                          <div className="item-name">{item.name}</div>
                          <div className="item-quantity">Quantity: {item.quantity}</div>
                          <div className="item-price">
                            {formatCurrency(item.discountedPrice || item.price, item.currency)} each
                          </div>
                          <div className="item-total">
                            Total: {formatCurrency((item.discountedPrice || item.price) * item.quantity, item.currency)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="order-section">
                  <h3>Order Summary</h3>
                  <div className="summary-item">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(selectedOrder.subtotal, selectedOrder.currency)}</span>
                  </div>
                  {selectedOrder.shipping > 0 && (
                    <div className="summary-item">
                      <span>Shipping:</span>
                      <span>{formatCurrency(selectedOrder.shipping, selectedOrder.currency)}</span>
                    </div>
                  )}
                  {selectedOrder.tax > 0 && (
                    <div className="summary-item">
                      <span>Tax:</span>
                      <span>{formatCurrency(selectedOrder.tax, selectedOrder.currency)}</span>
                    </div>
                  )}
                  {selectedOrder.discount > 0 && (
                    <div className="summary-item">
                      <span>Discount:</span>
                      <span>-{formatCurrency(selectedOrder.discount, selectedOrder.currency)}</span>
                    </div>
                  )}
                  <div className="summary-item total">
                    <span>Total:</span>
                    <span>{formatCurrency(selectedOrder.total, selectedOrder.currency)}</span>
                  </div>
                </div>

                <div className="order-section">
                  <h3>Status & Payment</h3>
                  <div className="status-controls">
                    <div className="control-group">
                      <label>Order Status:</label>
                      <select
                        value={selectedOrder.status}
                        onChange={(e) => handleUpdateOrder(selectedOrder._id, { status: e.target.value })}
                        className="status-select"
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="processing">Processing</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                    <div className="control-group">
                      <label>Shipping Status:</label>
                      <select
                        value={selectedOrder.shippingStatus}
                        onChange={(e) => handleUpdateOrder(selectedOrder._id, { shippingStatus: e.target.value })}
                        className="status-select"
                      >
                        <option value="not_shipped">Not Shipped</option>
                        <option value="preparing">Preparing</option>
                        <option value="shipped">Shipped</option>
                        <option value="out_for_delivery">Out for Delivery</option>
                        <option value="delivered">Delivered</option>
                      </select>
                    </div>
                    <div className="control-group">
                      <label>Payment Status:</label>
                      <select
                        value={selectedOrder.payment.status}
                        onChange={(e) => handleUpdateOrder(selectedOrder._id, { payment: { status: e.target.value } })}
                        className="status-select"
                      >
                        <option value="pending">Pending</option>
                        <option value="processing">Processing</option>
                        <option value="completed">Completed</option>
                        <option value="failed">Failed</option>
                        <option value="refunded">Refunded</option>
                      </select>
                    </div>
                    <div className="control-group">
                      <label>Tracking Number:</label>
                      <input
                        type="text"
                        value={selectedOrder.trackingNumber || ''}
                        onChange={(e) => handleUpdateOrder(selectedOrder._id, { trackingNumber: e.target.value })}
                        className="tracking-input"
                        placeholder="Enter tracking number"
                      />
                    </div>
                    {selectedOrder.payment.razorpayOrderId && (
                      <div className="payment-info">
                        <div><strong>Razorpay Order ID:</strong> {selectedOrder.payment.razorpayOrderId}</div>
                        {selectedOrder.payment.razorpayPaymentId && (
                          <div><strong>Payment ID:</strong> {selectedOrder.payment.razorpayPaymentId}</div>
                        )}
                        {selectedOrder.payment.paidAt && (
                          <div><strong>Paid At:</strong> {new Date(selectedOrder.payment.paidAt).toLocaleString()}</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {selectedOrder.notes && (
                  <div className="order-section">
                    <h3>Notes</h3>
                    <p>{selectedOrder.notes}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <div className="modal-actions">
                <button
                  onClick={() => handleDownloadInvoice(selectedOrder._id)}
                  className="btn btn-primary"
                >
                  📄 Download Invoice
                </button>
                <button
                  onClick={() => handleEmailInvoice(selectedOrder._id)}
                  className="btn btn-secondary"
                >
                  📧 Email Invoice
                </button>
                <button onClick={() => setShowOrderModal(false)} className="btn btn-secondary">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Workshop Booking Detail Modal */}
      {showBookingModal && selectedBooking && (
        <div className="modal-overlay" onClick={() => setShowBookingModal(false)}>
          <div className="modal-content order-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Workshop Booking Details</h2>
              <button className="modal-close" onClick={() => setShowBookingModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="order-details-grid">
                <div className="order-section">
                  <h3>Workshop Information</h3>
                  <div className="detail-item">
                    <strong>Workshop:</strong> {selectedBooking.workshop?.title || 'N/A'}
                  </div>
                  {selectedBooking.workshop?.date && (
                    <div className="detail-item">
                      <strong>Date:</strong> {formatDate(selectedBooking.workshop.date)}
                    </div>
                  )}
                  {selectedBooking.workshop?.time && (
                    <div className="detail-item">
                      <strong>Time:</strong> {selectedBooking.workshop.time}
                    </div>
                  )}
                  {selectedBooking.workshop?.price && (
                    <div className="detail-item">
                      <strong>Price:</strong> {formatCurrency(selectedBooking.workshop.price, selectedBooking.workshop.currency)}
                    </div>
                  )}
                </div>

                <div className="order-section">
                  <h3>Customer Information</h3>
                  <div className="detail-item">
                    <strong>Name:</strong> {selectedBooking.name}
                  </div>
                  <div className="detail-item">
                    <strong>Email:</strong> {selectedBooking.email}
                  </div>
                  <div className="detail-item">
                    <strong>Mobile:</strong> {selectedBooking.mobile}
                  </div>
                  <div className="detail-item">
                    <strong>Age:</strong> {selectedBooking.age}
                  </div>
                  <div className="detail-item">
                    <strong>Skill Level:</strong> 
                    <span className="badge badge-info" style={{ marginLeft: '8px' }}>
                      {selectedBooking.skillLevel}
                    </span>
                  </div>
                </div>

                <div className="order-section">
                  <h3>Booking Status</h3>
                  <div className="status-controls">
                    <div className="control-group">
                      <label>Booking Status:</label>
                      <select
                        value={selectedBooking.status}
                        onChange={(e) => handleUpdateBookingStatus(selectedBooking._id, e.target.value)}
                        className="status-select"
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                    <div className="control-group">
                      <label>Payment Status:</label>
                      <span className={`badge ${getPaymentBadgeClass(selectedBooking.paymentStatus)}`}>
                        {selectedBooking.paymentStatus}
                      </span>
                    </div>
                  </div>
                  {selectedBooking.notes && (
                    <div className="detail-item" style={{ marginTop: '16px' }}>
                      <strong>Notes:</strong>
                      <p style={{ marginTop: '8px', whiteSpace: 'pre-wrap' }}>{selectedBooking.notes}</p>
                    </div>
                  )}
                </div>

                <div className="order-section">
                  <h3>Booking Details</h3>
                  <div className="detail-item">
                    <strong>Booking ID:</strong> #{selectedBooking._id.toString().slice(-8)}
                  </div>
                  <div className="detail-item">
                    <strong>Booked At:</strong> {formatDate(selectedBooking.createdAt)}
                  </div>
                  {selectedBooking.paymentId && (
                    <div className="detail-item">
                      <strong>Payment ID:</strong> {selectedBooking.paymentId}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowBookingModal(false)} className="btn btn-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'danger' })}
        confirmText="Cancel Order"
        cancelText="Keep Order"
        type={confirmModal.type}
      />
    </div>
  );
};

export default OrderManager;

